import { getCsrfToken } from '../auth/authApi'
import { apiFetch } from '../../shared/http/apiFetch'
import type {
  AdministrationSnapshot,
  CompanyRequest,
  LicenseRequest,
  UserRequest,
} from './types'

function baseUrl(configurationId?: string) {
  return configurationId
    ? `/api/v1/apl/configurations/${configurationId}/administration`
    : '/api/v1/administration'
}

export async function getAdministration(
  configurationId?: string,
): Promise<AdministrationSnapshot> {
  const response = await apiFetch(baseUrl(configurationId), {
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error('administration.loadError')
  }
  return response.json() as Promise<AdministrationSnapshot>
}

async function command(
  path: string,
  method: 'POST' | 'PUT',
  body?: unknown,
): Promise<string> {
  const csrfToken = await getCsrfToken()
  const response = await apiFetch(path, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const payload = (await response.json().catch(() => null)) as {
    code?: string
  } | null
  if (!response.ok) {
    throw new Error(payload?.code ?? 'administration.actionError')
  }
  return payload?.code ?? 'administration.actionCompleted'
}

export const createCompany = (
  request: CompanyRequest,
  configurationId?: string,
) => command(`${baseUrl(configurationId)}/companies`, 'POST', request)

export const updateCompany = (
  companyId: string,
  request: CompanyRequest,
  configurationId?: string,
) => command(
  `${baseUrl(configurationId)}/companies/${companyId}`,
  'PUT',
  request,
)

export const seedCompanyDemoData = (
  companyId: string,
  configurationId: string,
) => command(
  `${baseUrl(configurationId)}/companies/${companyId}/demo-data`,
  'POST',
)

export const clearCompanyData = (
  companyId: string,
  configurationId: string,
) => command(
  `${baseUrl(configurationId)}/companies/${companyId}/clear-data`,
  'POST',
)

export const createUser = (
  request: UserRequest,
  configurationId?: string,
) => command(`${baseUrl(configurationId)}/users`, 'POST', request)

export const updateUser = (
  userId: string,
  request: UserRequest,
  configurationId?: string,
) => command(
  `${baseUrl(configurationId)}/users/${userId}`,
  'PUT',
  request,
)

export const resetPassword = (
  userId: string,
  temporaryPassword: string,
  configurationId?: string,
) => command(
  `${baseUrl(configurationId)}/users/${userId}/password-reset`,
  'POST',
  { temporaryPassword },
)

export const createLicense = (
  request: LicenseRequest,
  configurationId: string,
) => command(
  `${baseUrl(configurationId)}/licenses`,
  'POST',
  request,
)

export const updateLicense = (
  licenseId: string,
  request: LicenseRequest,
  configurationId: string,
) => command(
  `${baseUrl(configurationId)}/licenses/${licenseId}`,
  'PUT',
  request,
)

export const revokeSession = (
  sessionId: string,
  configurationId?: string,
) => command(
  `${baseUrl(configurationId)}/sessions/${sessionId}/revoke`,
  'POST',
)

export const savePermissionRole = (
  roleId: string | null,
  request: { name: string; description: string; permissions: Record<string, string> },
  configurationId?: string,
) => command(`${baseUrl(configurationId)}/permission-roles${roleId ? `/${roleId}` : ''}`,
  roleId ? 'PUT' : 'POST', request)

export const assignUserRoles = (userId: string, roleIds: string[], configurationId?: string) =>
  command(`${baseUrl(configurationId)}/users/${userId}/permission-roles`, 'PUT', { roleIds })
